import { Button, Dialog, Input, Select, useToast } from '@zyxantech/tux'
import { useState } from 'react'
import { useWriteTagValueMutation, type Tag } from '@/api/tags.api'

export interface TagWriteDialogProps {
  tag: Tag | null
  open: boolean
  onClose: () => void
}

/** Coerce the string form value into the type the tag expects. */
function coerce(raw: string, dataType: string): number | boolean | string {
  if (dataType === 'boolean') return raw === 'true'
  if (
    dataType === 'integer' ||
    dataType === 'float' ||
    dataType === 'real' ||
    dataType === 'number'
  ) {
    const n = Number(raw)
    return Number.isFinite(n) ? n : raw
  }
  return raw
}

/**
 * Confirmation dialog for writing a value to a read/write tag. Writing to a live
 * device is a deliberate action — the user types the value and explicitly confirms.
 */
export function TagWriteDialog({ tag, open, onClose }: TagWriteDialogProps) {
  const [writeTagValue, { isLoading }] = useWriteTagValueMutation()
  const toast = useToast()
  const [value, setValue] = useState('')

  if (!tag) return null

  const isBool = tag.dataType === 'boolean'

  const handleConfirm = async () => {
    try {
      await writeTagValue({ id: tag.id, body: { value: coerce(value, tag.dataType) } }).unwrap()
      toast.show(`Wrote ${value || '—'} to ${tag.name}`, 'success')
      setValue('')
      onClose()
    } catch {
      toast.show(`Failed to write to ${tag.name}`, 'error')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Write to ${tag.name}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={isLoading} onClick={() => void handleConfirm()}>
            Write value
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-text-secondary">
          This writes directly to the device tag{' '}
          <span className="font-mono text-text-primary">{tag.address}</span>. Confirm the value
          below.
        </p>
        {isBool ? (
          <Select
            value={value || 'false'}
            onChange={(v) => setValue(typeof v === 'string' ? v : (v[0] ?? 'false'))}
            options={[
              { value: 'true', label: 'ON (true)' },
              { value: 'false', label: 'OFF (false)' },
            ]}
          />
        ) : (
          <Input
            label={`New value${tag.unit ? ` (${tag.unit})` : ''}`}
            type={tag.dataType === 'string' ? 'text' : 'number'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter value to write"
            autoFocus
          />
        )}
      </div>
    </Dialog>
  )
}
